(() => {
  if (customElements.get('latrand-brand-carousel')) return;

  class LatrandBrandCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.grid = this.querySelector('[data-carousel-grid]');
      this.items = this.grid ? Array.from(this.grid.children).filter(item => item.hasAttribute('data-carousel-item')) : [];
      if (!this.grid || !this.items.length) return;
      this.items.forEach(item => {
        const name = item.dataset.brandName;
        if (!name) return;
        item.querySelectorAll('a[href]').forEach(link => {
          if (!link.getAttribute('aria-label')?.trim() && !link.textContent.trim()) link.setAttribute('aria-label', name);
        });
        item.querySelectorAll('img').forEach(image => {
          if (!image.alt.trim()) image.alt = name;
        });
      });
      this.initialized = true;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.toolbar = this.querySelector('[data-carousel-toolbar]');
      this.navigation = this.querySelector('[data-carousel-navigation]');
      this.toggle = this.querySelector('[data-carousel-toggle]');
      this.status = this.querySelector('[data-carousel-status]');
      this.pauseIcon = this.querySelector('[data-carousel-pause]');
      this.playIcon = this.querySelector('[data-carousel-play]');
      this.rows = this.clamp(this.dataset.rows, 1, 3);
      this.interval = this.clamp(this.dataset.interval, 0, 10) * 1000;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motionQuery.matches;
      this.explicitPlay = false;
      this.pointerInput = false;
      this.hovered = false;
      this.inViewport = !('IntersectionObserver' in window);
      this.page = 0;
      this.pageSize = 0;
      this.pages = 1;
      this.minHeight = 0;
      this.width = 0;

      this.toggle?.addEventListener('click', () => {
        const paused = this.isPaused();
        this.userPaused = !paused;
        this.explicitPlay = paused;
        this.syncToggle();
        this.schedule();
      }, { signal });
      this.querySelector('[data-carousel-previous]')?.addEventListener('click', () => this.move(-1), { signal });
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.move(1), { signal });
      this.addEventListener('keydown', event => this.onKeydown(event), { signal });
      document.addEventListener('keydown', event => {
        if (event.key === 'Tab') {
          this.pointerInput = false;
          this.schedule();
        }
      }, { signal });
      this.addEventListener('focusin', () => this.schedule(), { signal });
      this.addEventListener('focusout', () => queueMicrotask(() => {
        if (this.initialized) this.schedule();
      }), { signal });
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') {
          this.hovered = true;
          this.schedule();
        }
      }, { signal });
      this.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse') {
          this.hovered = false;
          this.schedule();
        }
      }, { signal });
      this.addEventListener('pointerdown', event => this.onPointerdown(event), { signal });
      this.addEventListener('pointermove', event => this.onPointermove(event), { signal });
      this.addEventListener('pointerup', event => this.onPointerup(event), { signal });
      this.addEventListener('pointercancel', () => this.resetGesture(), { signal });
      window.addEventListener('pointerup', event => this.onPointerup(event), { signal });
      window.addEventListener('pointercancel', () => this.resetGesture(), { signal });
      window.addEventListener('blur', () => this.resetGesture(), { signal });
      this.grid.addEventListener('lostpointercapture', () => this.resetGesture(), { signal });
      this.addEventListener('click', event => {
        if (this.suppressClick && event.detail > 0) {
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        }
      }, { signal, capture: true });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.onMotionChange = () => {
        if (this.motionQuery.matches) this.explicitPlay = false;
        this.syncToggle();
        this.schedule();
      };
      this.motionQuery.addEventListener('change', this.onMotionChange);
      this.addEventListener('shopify:block:select', event => {
        const item = event.target.closest?.('[data-carousel-item]');
        const index = this.items.indexOf(item);
        if (index >= 0 && this.active) {
          this.userPaused = true;
          this.page = Math.floor(index / this.pageSize);
          this.render(false);
          this.syncToggle();
          this.schedule();
        }
      }, { signal });
      this.addEventListener('load', () => this.reserveHeight(), { signal, capture: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(entries => {
          const width = entries[0]?.contentRect.width || this.getBoundingClientRect().width;
          if (Math.abs(width - this.width) > .5) this.refresh(width);
        });
        this.resizeObserver.observe(this);
      }
      window.addEventListener('resize', () => this.refresh(this.getBoundingClientRect().width), { signal });
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(entries => {
          this.inViewport = entries.some(entry => entry.isIntersecting);
          this.schedule();
        }, { threshold: .05 });
        this.intersectionObserver.observe(this);
      }
      this.refresh(this.getBoundingClientRect().width);
      this.syncToggle();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.initialized = false;
      this.controller.abort();
      this.motionQuery.removeEventListener('change', this.onMotionChange);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.clickTimer);
      this.gesture = null;
      this.items.forEach(item => this.setItemVisible(item, true));
      this.grid.style.minHeight = '';
      this.toolbar.hidden = true;
      this.navigation.hidden = true;
      this.removeAttribute('data-initialized');
    }

    clamp(value, min, max) {
      return Math.max(min, Math.min(max, Number(value) || 0));
    }

    refresh(width) {
      if (!this.initialized) return;
      const style = getComputedStyle(this);
      const columns = this.clamp(style.getPropertyValue('--brand-carousel-columns'), 1, 8);
      const active = Number(style.getPropertyValue('--brand-carousel-active')) === 1;
      const size = columns * this.rows;
      if (this.width === width && this.pageSize === size && this.active === active) return;
      const firstItem = this.page * this.pageSize;
      this.width = width;
      this.active = active;
      this.pageSize = size;
      this.pages = active ? Math.ceil(this.items.length / size) : 1;
      this.page = Math.min(this.pages - 1, Math.floor(firstItem / size));
      this.minHeight = 0;
      this.grid.style.minHeight = '';
      this.render(false);
      this.setAttribute('data-initialized', '');
      this.schedule();
    }

    setItemVisible(item, visible) {
      item.hidden = !visible;
      item.inert = !visible;
      if (visible) item.removeAttribute('aria-hidden');
      else item.setAttribute('aria-hidden', 'true');
    }

    render(announce) {
      const first = this.page * this.pageSize;
      const focused = document.activeElement;
      const focusedItem = focused?.closest?.('[data-carousel-item]');
      const hideFocused = focusedItem && this.items.includes(focusedItem)
        && this.active && (this.items.indexOf(focusedItem) < first || this.items.indexOf(focusedItem) >= first + this.pageSize);
      this.items.forEach((item, index) => this.setItemVisible(item, !this.active || (index >= first && index < first + this.pageSize)));
      const paginated = this.active && this.pages > 1;
      this.toolbar.hidden = !paginated || this.interval === 0;
      this.navigation.hidden = !paginated || this.dataset.showArrows !== 'true';
      this.status.setAttribute('aria-live', announce ? 'polite' : 'off');
      this.status.textContent = `${this.page + 1} / ${this.pages}`;
      this.status.setAttribute('aria-label', this.dataset.pageLabel.replace('__CURRENT__', String(this.page + 1)).replace('__TOTAL__', String(this.pages)));
      this.reserveHeight();
      if (hideFocused) {
        const target = this.items[first]?.querySelector('a[href], button, [tabindex="0"]');
        target?.focus({ preventScroll: true });
      }
    }

    reserveHeight() {
      if (!this.initialized || !this.active || this.pages <= 1) return;
      this.minHeight = Math.max(this.minHeight, this.grid.getBoundingClientRect().height);
      this.grid.style.minHeight = `${this.minHeight}px`;
    }

    move(direction, announce = true) {
      if (!this.active || this.pages <= 1) return;
      this.page = (this.page + direction + this.pages) % this.pages;
      this.render(announce);
      this.schedule();
    }

    isPaused() {
      return this.userPaused || (this.motionQuery.matches && !this.explicitPlay);
    }

    syncToggle() {
      const paused = this.isPaused();
      this.toggle.setAttribute('aria-pressed', String(paused));
      this.toggle.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.pauseIcon.hidden = paused;
      this.playIcon.hidden = !paused;
    }

    canAutoplay() {
      const focus = document.activeElement;
      const controlFocus = focus?.closest?.('[data-carousel-toggle], [data-carousel-previous], [data-carousel-next]');
      const focused = this.contains(focus) && !(controlFocus && this.pointerInput);
      return this.initialized && this.isConnected && this.active && this.pages > 1 && this.interval > 0
        && !this.isPaused() && !this.hovered && !focused && !this.gesture && !document.hidden && this.inViewport;
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.canAutoplay()) this.timer = setTimeout(() => {
        if (this.canAutoplay()) this.move(1, false);
        else this.schedule();
      }, this.interval);
    }

    onKeydown(event) {
      this.pointerInput = false;
      this.schedule();
      if (!this.active || this.pages <= 1 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      }
    }

    onPointerdown(event) {
      this.pointerInput = true;
      this.suppressClick = false;
      clearTimeout(this.clickTimer);
      if (event.button !== 0 || !event.isPrimary || !this.active || this.pages <= 1 || !this.grid.contains(event.target) || event.target.closest('button, input, select, textarea')) {
        this.schedule();
        return;
      }
      this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, horizontal: false };
      this.schedule();
    }

    onPointermove(event) {
      if (this.gesture?.id !== event.pointerId) return;
      const dx = event.clientX - this.gesture.x;
      const dy = event.clientY - this.gesture.y;
      if (!this.gesture.horizontal && Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.25) {
        this.gesture.horizontal = true;
        this.grid.setPointerCapture?.(event.pointerId);
      }
      if (this.gesture.horizontal && event.cancelable) event.preventDefault();
    }

    onPointerup(event) {
      if (this.gesture?.id !== event.pointerId) return;
      const gesture = this.gesture;
      this.gesture = null;
      if (this.grid.hasPointerCapture?.(event.pointerId)) this.grid.releasePointerCapture(event.pointerId);
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (gesture.horizontal) {
        this.suppressClick = true;
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 500);
      }
      if (gesture.horizontal && Math.abs(dx) >= Math.max(40, this.width * .08) && Math.abs(dx) > Math.abs(dy) * 1.25) {
        const rtl = getComputedStyle(this).direction === 'rtl';
        this.move((dx < 0 ? 1 : -1) * (rtl ? -1 : 1));
      } else this.schedule();
    }

    resetGesture() {
      const id = this.gesture?.id;
      this.gesture = null;
      if (id !== undefined && this.grid.hasPointerCapture?.(id)) this.grid.releasePointerCapture(id);
      this.schedule();
    }
  }

  customElements.define('latrand-brand-carousel', LatrandBrandCarousel);
})();
